<template>
  <div>
    <!-- Full Screen Loader -->
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#68D389"
      spinner="bar-fade-scale"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackBar" color="#68D389" right :timeout="5000">
      <v-layout wrap justify-center>
        <v-flex text-left align-self-center>{{ msg }}</v-flex>
        <v-flex text-right>
          <v-btn small text @click="showSnackBar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Main Content -->
    <v-container fluid class="py-4 py-md-8 px-3 px-md-6">
      <v-row justify="center">
        <v-col cols="12" sm="12" md="10" lg="8" xl="6">
          <v-card elevation="6" class="rounded-xl">
            <v-card-title class="white--text py-4 py-md-6" style="background: linear-gradient(135deg, #68D389, #4CAF50);">
              <v-icon large left color="white" class="mr-2 mr-md-3">mdi-magnify</v-icon>
              <span class="text-h6 text-md-h5">Search Ticket by Number</span>
            </v-card-title>

            <v-card-text class="pa-4 pa-md-6">
              <v-row align="center" no-gutters>
                <v-col cols="12" md="8" class="pr-0 pr-md-3 mb-3 mb-md-0">
                  <v-text-field
                    v-model="ticketNumber"
                    label="Enter Ticket Number"
                    prepend-inner-icon="mdi-ticket-confirmation"
                    outlined
                    dense
                    clearable
                    color="#68D389"
                    autofocus
                    hide-details
                    @keyup.enter="searchTicket"
                    @click:clear="clearResults"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-btn
                    @click="searchTicket"
                    color="#68D389"
                    dark
                    block
                    large
                    :loading="appLoading"
                    :disabled="!ticketNumber || ticketNumber.trim() === ''"
                    class="text-none font-weight-bold"
                    elevation="2"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Results Table -->
      <v-row v-if="tickets.length > 0" justify="center" class="mt-4 mt-md-8">
        <v-col cols="12" sm="12" md="10" lg="10" xl="9">
          <v-card elevation="6" class="rounded-xl overflow-hidden">
            <v-card-title class="py-4 px-5 white--text" style="background: linear-gradient(135deg, #2E7D32, #68D389);">
              <v-icon left color="white" class="mr-2">mdi-ticket-confirmation</v-icon>
              <span class="text-subtitle-1 text-md-h6 font-weight-bold">Search Results</span>
              <v-spacer></v-spacer>
              <v-chip small color="white" text-color="#2E7D32" class="font-weight-bold">
                {{ totalItems || tickets.length }} found
              </v-chip>
            </v-card-title>

            <!-- Mobile View -->
            <div class="d-block d-md-none pa-3">
              <v-card
                v-for="(item, index) in tickets"
                :key="item._id"
                outlined
                class="rounded-lg mb-3"
              >
                <v-card-text class="py-3">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div>
                      <span class="text-caption grey--text mr-1">#{{ (currentPage - 1) * 10 + index + 1 }}</span>
                      <v-chip small label color="#E8F5E9" text-color="#2E7D32" class="font-weight-bold">
                        Ticket #{{ item.ticket.ticketNo }}
                      </v-chip>
                    </div>
                    <span class="text-subtitle-1 font-weight-bold" style="color:#2E7D32">
                      ₹{{ item.total ? item.total.toLocaleString('en-IN') : 0 }}
                    </span>
                  </div>
                  <div class="d-flex" style="gap:8px">
                    <v-btn small color="#68D389" dark @click="viewTicketPDF(item._id)" class="text-none flex-grow-1">
                      <v-icon small left>mdi-eye</v-icon>View
                    </v-btn>
                    <v-btn small outlined color="#2E7D32" @click="openHistory(item.ticket.ticketNo)" class="text-none flex-grow-1">
                      <v-icon small left>mdi-history</v-icon>History
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Desktop View -->
            <v-simple-table class="d-none d-md-block results-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">#</th>
                    <th class="text-left">Ticket No</th>
                    <th class="text-left">Amount</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in tickets" :key="item._id">
                    <td class="text-center grey--text">{{ (currentPage - 1) * 10 + index + 1 }}</td>
                    <td>
                      <v-chip small label color="#E8F5E9" text-color="#2E7D32" class="font-weight-bold">
                        #{{ item.ticket.ticketNo }}
                      </v-chip>
                    </td>
                    <td class="font-weight-bold" style="color:#2E7D32">
                      ₹{{ item.total ? item.total.toLocaleString('en-IN') : 0 }}
                    </td>
                    <td class="text-center">
                      <v-btn small color="#68D389" dark @click="viewTicketPDF(item._id)" class="text-none mr-2">
                        <v-icon small left>mdi-eye</v-icon>View Ticket
                      </v-btn>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn small outlined color="#2E7D32" v-bind="attrs" v-on="on"
                            @click="openHistory(item.ticket.ticketNo)" class="text-none">
                            <v-icon small left>mdi-history</v-icon>History
                          </v-btn>
                        </template>
                        <span>Booking history &amp; details</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Pagination -->
            <v-card-actions v-if="pages > 1" class="justify-center pa-4">
              <v-pagination
                v-model="currentPage"
                :length="pages"
                :total-visible="$vuetify.breakpoint.xs ? 5 : 7"
                color="#68D389"
                @input="searchTicket"
              ></v-pagination>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <v-row v-else-if="!appLoading && searched" justify="center">
        <v-col cols="12" sm="10" md="8" lg="6" class="text-center py-8 py-md-12">
          <v-icon size="80" class="mb-4" color="grey lighten-2">mdi-ticket-confirmation-outline</v-icon>
          <h2 class="text-h5 text-md-h4 mt-4 mb-3 grey--text text--darken-1">No ticket found</h2>
          <p class="text-body-1 grey--text px-4">
            "{{ ticketNumber }}" - No matching ticket found.
          </p>
        </v-col>
      </v-row>
    </v-container>


    <!-- Shared booking history dialog -->
    <BookingHistoryDialog ref="historyDlg" />
  </div>
</template>

<script>
import axios from "axios";
import BookingHistoryDialog from "../Booking/bookingHistoryDialog";

export default {
  components: { BookingHistoryDialog },
  data() {
    return {
      appLoading: false,
      showSnackBar: false,
      msg: "",
      ticketNumber: "",
      tickets: [],
      currentPage: 1,
      pages: 0,
      totalItems: 0,
      searched: false,
    };
  },

  methods: {
    async searchTicket() {
      if (!this.ticketNumber || this.ticketNumber.trim() === "") {
        this.msg = "Please enter a ticket number";
        this.showSnackBar = true;
        return;
      }

      this.appLoading = true;
      this.searched = true;

      try {
        const response = await axios.get("/report/getpaymentsbyticketnumber", {
          headers: {
            token: localStorage.getItem("token"),
          },
          params: {
            ticketNumber: this.ticketNumber.trim(),
            page: this.currentPage,
            limit: 10,
          },
        });

        if (response.data.status) {
          this.tickets = response.data.data || [];
          this.pages = response.data.pages || 0;
          this.totalItems = response.data.total || 0;

          if (this.tickets.length === 0) {
            this.msg = "No ticket found with this number";
            this.showSnackBar = true;
          }
        } else {
          this.tickets = [];
          this.msg = response.data.msg || "Something went wrong";
          this.showSnackBar = true;
        }
      } catch (error) {
        this.tickets = [];
        this.msg = "Failed to search. Please try again.";
        this.showSnackBar = true;
        console.error(error);
      } finally {
        this.appLoading = false;
      }
    },

    viewTicketPDF(id) {
      const url = `${this.baseURL}/booking/ticketpdf/?id=${id}`;
      window.open(url, "_blank");
    },

    openHistory(ticketNo) {
      this.$refs.historyDlg.open(ticketNo);
    },

    clearResults() {
      this.ticketNumber = "";
      this.tickets = [];
      this.currentPage = 1;
      this.searched = false;
    },
  },
};
</script>

<style scoped>
.results-table >>> th {
  font-weight: 600 !important;
  font-size: 13px !important;
  color: #2e7d32 !important;
  background-color: #f1f8e9;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.results-table >>> td {
  font-size: 14px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.results-table >>> tbody tr:hover {
  background-color: #f9fbf7 !important;
}

/* Responsive text sizing */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1.1rem !important;
  }
}

/* Better spacing on mobile */
@media (max-width: 960px) {
  .v-container {
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>